import React, { useEffect, useRef, useState } from "react";

import { Header } from "../components/product/Header";
import { Options } from "../components/product/Options";
import { CartButton } from "../components/product/CartButton";
import { Shipping } from "../components/product/Shipping";
import { Consultant } from "../components/product/Consultant";
import { Info } from "../components/product/Info";
import { Docs } from "../components/product/Docs";
import { Sku } from "../components/product/Sku";
import { Price } from "../components/product/Price";
import { WishlistButton } from "../components/product/buttons/Wishlist";
import { Notification } from "../components/product/buttons/Notification";
import { openCredit } from "../stores/credit/credit.reducer";
import { useProductDispatch, useProductSelector } from "../stores/product/product.store";
import { ProductState } from "../stores/product/product-state.interface";
import { setProduct, addToCart, gtag, syncWishlistFromCookie } from "../stores/product/product.reducer";
import { ProductInterface, Subscription as SubscriptionInterface } from "../interfaces/product/product.interface";
import { MetaBlock } from "../components/product/MetaBlock";
import { ConsultantInfo } from "../components/product/ConsultantInfo";
import Images from "../components/product/Images";
import CreditPage from "./Credit";
import { Subscription } from "../components/product/Subscription";
import ProductActualize from "../components/product/Actualize";
import {NotInStockButtons} from "../components/product/NotInStockButtons";
import {NotificationModal} from "../components/product/NotificationModal";
import {initProduct} from "../stores/product/thunks/init-product.thunk";

export const Product: React.FC = () => {
    const translate = useProductSelector((state: ProductState) => state.translate);
    const product: ProductInterface = useProductSelector((state: ProductState) => state.product);
    const dispatch = useProductDispatch();
    console.log('product.subscriptions:', product.subscriptions);

    // Show all subscriptions state
    const [showAllSubscriptions, setShowAllSubscriptions] = useState(false);
    const toggleShowAllSubscriptions = () => setShowAllSubscriptions(prev => !prev);

    // Selected subscription
    const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionInterface | undefined>(undefined);

    useEffect(() => {
        if (!product.subscription && product.subscriptions?.length) {
            const firstSub = product.subscriptions[0];
            setSelectedSubscription(firstSub);

            dispatch(setProduct({
                product: { ...product, subscription: firstSub },
                translate
            }));
        }
    }, [product.subscriptions, product.subscription, dispatch, product, translate]);

    const handleSubscriptionChange = (sub: SubscriptionInterface) => {
        setSelectedSubscription(sub);
        dispatch(setProduct({ product: { ...product, subscription: sub }, translate }));
    };

    const subscriptionsToShow = showAllSubscriptions
        ? product.subscriptions ?? []
        : (product.subscriptions ?? []).slice(0, 2);

    //const [number, setNumber] = useState("076770088");
    const [number, setNumber] = useState("079002751");
    const characteristicsRef = useRef<HTMLDivElement>(null);
    const [isShowCharacteristic, setIsShowCharacteristic] = useState(!product.description.length);
    const addToCartRef = useRef<HTMLDivElement>(null);
    const [isFixedVisible, setIsFixedVisible] = useState(false);

    // Scroll helper
    const scrollToElementWithOffset = (element: HTMLElement, offset: number) => {
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: elementPosition - offset, behavior: "smooth" });
    };

    const showCharacteristic = () => {
        if (characteristicsRef.current) {
            scrollToElementWithOffset(characteristicsRef.current, 200);
            setIsShowCharacteristic(true);
        }
    };

    const fetchPhone = () => {
        let phonesScript: string | null = localStorage.getItem("roistat_phone_script_data");
        if (!phonesScript && (window as any).roistatGetCookie) {
            phonesScript = (window as any).roistatGetCookie("roistat_phone_script_data");
        }
        if (phonesScript) {
            try {
                const phoneData = JSON.parse(phonesScript);
                if (phoneData.length > 0) setNumber(phoneData[0].phone);
            } catch {}
        }
    };

    const open = (id: number, month: number) => dispatch(openCredit({ id, month }));
    const toCart = () => dispatch(addToCart());

    useEffect(() => {
        dispatch(initProduct());
        fetchPhone();

        const observer = new IntersectionObserver(
            entries => setIsFixedVisible(!entries[0].isIntersecting),
            { threshold: 0.1 }
        );

        if (addToCartRef.current) observer.observe(addToCartRef.current);

        dispatch(gtag("mn_item_view"));

        const timeouts = [
            setTimeout(() => dispatch(gtag("mn_item_30sec")), 30000),
            setTimeout(() => dispatch(gtag("mn_item_60sec")), 60000),
            setTimeout(() => dispatch(gtag("mn_item_120sec")), 120000),
            setTimeout(() => dispatch(gtag("mn_item_300sec")), 300000),
            setTimeout(() => dispatch(gtag("mn_item_600sec")), 600000)
        ];

        return () => {
            if (addToCartRef.current) observer.disconnect();
            timeouts.forEach(clearTimeout);
        };
    }, []);

    useEffect(() => {
        const sync = () => dispatch(syncWishlistFromCookie());
        document.addEventListener("wishlist.count.changed", sync);
        return () => document.removeEventListener("wishlist.count.changed", sync);
    }, [dispatch]);

    // Subscriptions to render (SSR-safe)
    const subscriptionsToRender = product.subscriptions?.filter(
        sub => showAllSubscriptions || sub.expand
    ) || [];

    // Notifications
    const [isOpenNotification, setIsOpenNotification] = useState(false);
    const showNotification = () => {
        setIsOpenNotification(true);
    }
    const closeNotification = () => {
        setIsOpenNotification(false);
    }


    return (
        <>
            <Header product={product} />
            <div className="md:flex mt-8">
                {/* MOBILE */}
                <div className="md:w-2/3 md:pr-5">
                    <div className="md:grid md:grid-cols-2 md:gap-4">
                        <div className="flex justify-end md:hidden">
                            <WishlistButton />
                        </div>
                        <Images product={product} />
                        {!product.inStock && (
                            <p className="text-[16px] text-gray font-semibold mt-3 md:hidden">{translate.noInStock}</p>
                        )}
                        <h1 className="font-semibold text-[20px] mt-3 md:hidden">{product.name}</h1>
                        <Sku product={product} className="flex md:hidden mb-5 justify-between" />
                        <div className="md:hidden">
                            {(subscriptionsToShow?.length ?? 0) > 0 ? (
                                subscriptionsToShow.map(sub => (
                                    <Subscription
                                        key={sub.position}
                                        product={product}
                                        subscription={sub}
                                        selectedSubscription={selectedSubscription}
                                        onSelectSubscription={handleSubscriptionChange}
                                        translate={translate}
                                    />
                                ))
                            ) : (
                                <Price product={product}/>
                            )}
                            <ProductActualize needUpdate={ product.needUpdate } lastActualize={ product.actualizat } inStock={product.inStock}/>
                            {(product.subscriptions?.length ?? 0) > 2 && (
                                <div
                                    className="pr-2 w-full cursor-pointer flex items-center justify-end gap-2"
                                    onClick={toggleShowAllSubscriptions}
                                >
                                            <span className="subscriptions-expand">
                                                {showAllSubscriptions ? translate.hideSubscriptions : translate.showAllSubscriptions}
                                            </span>
                                    <svg
                                        width={22}
                                        height={24}
                                        viewBox="0 0 22 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className={`transition-transform duration-300 origin-center ${
                                            showAllSubscriptions ? "rotate-180" : "rotate-0"
                                        }`}
                                    >
                                        <path d="M19 9l-7 7-7-7"/>
                                    </svg>
                                </div>
                            )}
                        </div>

                        {product.merchantExternal ? (
                            <div className="md:hidden mb-5">{translate.vandute} {product.merchant}</div>
                        ) : <div className="md:hidden mb-5"></div>}

                        <Options product={product} />
                    </div>

                    <div className="md:hidden">
                        <Shipping inStock={ product.inStock } needUpdate={ product.needUpdate } shipping={product.shipping}/>
                        <div ref={addToCartRef}>
                            <div className="bg-[#f7f8fa] p-[16px] md:p-[24px] rounded-2xl mb-4">
                                <div className="bg-[#f7f8fa] p-[0px] md:p-[24px] rounded-2xl mb-0 mt-2">
                                    {product.inStock
                                        ? <CartButton product={product} onClick={toCart}/>
                                        : <NotInStockButtons onClickNotification={showNotification} similareLink={product.similareLink} />
                                    }
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="md:hidden mb-2">
                        <Consultant number={number} text={translate.consultant} numberText={translate.number}/>
                    </div>
                    {product.isConsultant && <div className="md:hidden mb-2"><ConsultantInfo product={product}/></div>}

                    <div className="mb-12 md:mt-24" ref={characteristicsRef}>
                        <Info product={product} isShowCharacteristic={isShowCharacteristic}/>
                    </div>

                    <Docs product={product}/>
                </div>

                {/*PC*/}
                <div className="pl-5 hidden md:block md:w-1/3">
                    <div className="bg-[#f7f8fa] p-[16px] md:p-[24px] rounded-2xl mb-4">
                        <div className={`flex ${product.subscriptions?.length === 0 ? 'flex-row justify-between' : 'flex-col'} w-full mb-3`}>
                        {(subscriptionsToShow?.length ?? 0) > 0 ? (
                                subscriptionsToShow.map(sub => (
                                    <Subscription
                                        key={sub.position}
                                        product={product}
                                        subscription={sub}
                                        selectedSubscription={selectedSubscription}
                                        onSelectSubscription={handleSubscriptionChange}
                                        translate={translate}
                                    />
                                ))
                            ) : (
                                <Price product={product} />
                            )}

                            {(product.subscriptions?.length ?? 0) > 2 && (
                                <div
                                    className="pr-2 w-full cursor-pointer flex items-center justify-end gap-2"
                                    onClick={toggleShowAllSubscriptions}
                                >
                                <span className="subscriptions-expand">
                                    {showAllSubscriptions ? translate.hideSubscriptions : translate.showAllSubscriptions}
                                </span>
                                    <svg
                                        width={22}
                                        height={24}
                                        viewBox="0 0 22 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className={`transition-transform duration-300 origin-center ${
                                            showAllSubscriptions ? "rotate-180" : "rotate-0"
                                        }`}
                                    >
                                        <path d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            )}
                            {product.subscriptions && product.subscriptions.length === 0 && <WishlistButton />}
                        </div>
                        <ProductActualize needUpdate={ product.needUpdate } lastActualize={ product.actualizat } inStock={ product.inStock } />
                        { product.inStock
                            ? <CartButton product={product} onClick={toCart} />
                            : <NotInStockButtons onClickNotification={ showNotification } similareLink={product.similareLink} />
                        }
                    </div>
                    <Shipping inStock={ product.inStock } needUpdate={ product.needUpdate } shipping={product.shipping} />
                    <Consultant number={number} text={translate.consultant} numberText={translate.number} />
                    {product.isConsultant && <ConsultantInfo product={product} />}
                </div>
            </div>

            <div className={`${!isFixedVisible && "hidden"} fixed bottom-[66px] z-[14] left-0 w-full bg-white md:hidden`}>
                {product.inStock
                    ? <div className="flex p-2">
                        <button onClick={() => open(1, 1)}
                                className="rounded-2xl p-4 w-full bg-[#f78122] font-bold m-1 min-h-[49px]">{ product.needUpdate ? '' : translate.credit}</button>
                        <button onClick={toCart}
                                className="rounded-2xl p-4 w-full bg-yellow font-bold m-1 min-h-[49px]">{ product.needUpdate ? '' : translate.cart}</button>
                    </div>
                    : <div className="flex p-2">
                        <a href={ product.similareLink } className="rounded-2xl p-4 w-full bg-[#f78122] font-bold m-1 inline-block text-center">
                            {translate.similare}
                        </a>
                        <button
                            onClick={ showNotification }
                            className="flex justify-center items-center bg-yellow font-bold w-full rounded-2xl p-4 m-1">
                            <svg width="25" height="24" viewBox="0 0 25 24" fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd"
                                      d="M6.49398 13V9C6.49398 5.686 9.18298 3 12.5 3C15.817 3 18.506 5.686 18.506 9V13C18.506 13.986 18.954 14.919 19.723 15.537L20.032 15.785C20.949 16.521 20.428 18 19.252 18H5.74798C4.57198 18 4.05098 16.521 4.96798 15.785L5.27698 15.537C6.04698 14.919 6.49398 13.986 6.49398 13Z"
                                      stroke="#12100B" strokeWidth="1.5" strokeLinecap="round"
                                      strokeLinejoin="round"/>
                                <path d="M11 22.0005H14" stroke="#12100B" strokeWidth="1.5" strokeLinecap="round"
                                      strokeLinejoin="round"/>
                            </svg>
                            {translate.notificationInStock}
                        </button>
                    </div>
                }
            </div>

            <CreditPage/>
            <NotificationModal isOpen={isOpenNotification} onClose={() => setIsOpenNotification(false)}
                               product={product}/>
            <MetaBlock product={product}/>
        </>
    );
};
